import { FC, useState } from "react";
import {
  Box,
  Flex,
  PrimaryButton,
  SecondaryButton,
  Text,
  Stack,
  UnderlineButton,
  IconButton,
} from "flicket-ui";
import styled from "styled-components";
import {
  useCookieConsent,
  CookiePreferences,
} from "~context/cookieConsentContext";
import { CookiePreferencesModal } from "./CookiePreferencesModal";
import { useOrganization } from "~hooks/useOrganization";
import { OrganizationFeatures } from "~lib/features";
import { Gear } from "@phosphor-icons/react";
import { useIsMounted } from "~hooks";

const BannerContainer = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${(p) => p.theme.colors.white};
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1500;
  padding: ${(p) => p.theme.space[4]}px;
  border-top: 3px solid ${(p) => p.theme.colors.P300};

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    max-width: 500px;
    bottom: ${(p) => p.theme.space[4]}px;
    left: ${(p) => p.theme.space[4]}px;
    border-radius: ${(p) => p.theme.radii.md};
    border: 2px solid ${(p) => p.theme.colors.P300};
  }
`;

export const CookieBanner: FC = () => {
  const [showSettings, setShowSettings] = useState(false);
  const { preferences, setPreferences, hasSetPreferences } = useCookieConsent();
  const { hasFeature } = useOrganization();
  const hasMounted = useIsMounted();

  const acceptAll = () => {
    setPreferences({
      essential: true,
      marketing: true,
      analytics: true,
    });
  };

  const denyAll = () => {
    setPreferences({
      essential: true,
      marketing: false,
      analytics: false,
    });
  };

  const savePreferences = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    setShowSettings(false);
  };

  if (!hasMounted || !hasFeature(OrganizationFeatures.CookieConsent)) {
    return null;
  }

  const showBanner = !hasSetPreferences && !showSettings;

  return (
    <>
      {showBanner && (
        <BannerContainer>
          <Stack direction="vertical" gap={3}>
            <Text variant="header.S">Cookie Preferences</Text>
            <Text variant="small">
              We use cookies to enhance your browsing experience, analyze site
              traffic, and serve personalized content. By clicking
              &quot;Accept&quot;, you consent to our use of cookies.
            </Text>

            <Flex
              flexDirection={["column", "column", "row"]}
              justifyContent={["flex-start", "flex-start", "space-between"]}
              alignItems={["stretch", "stretch", "center"]}
            >
              <Flex mb={[2, 2, 0]}>
                <SecondaryButton
                  onClick={denyAll}
                  mr={2}
                  flex={["1", "1", "none"]}
                >
                  Deny
                </SecondaryButton>
                <PrimaryButton onClick={acceptAll} flex={["1", "1", "none"]}>
                  Accept
                </PrimaryButton>
              </Flex>
              <UnderlineButton
                onClick={() => setShowSettings(true)}
                width={["100%", "100%", "auto"]}
              >
                Consent Settings
              </UnderlineButton>
            </Flex>
          </Stack>
        </BannerContainer>
      )}

      {!showBanner && (
        <Box position="fixed" bottom="20px" left="20px" zIndex={1000}>
          <IconButton
            variant="outline"
            isRounded
            onClick={() => setShowSettings(true)}
            aria-label="Cookie Preferences"
            title="Cookie Preferences"
            boxShadow="hover"
          >
            <Gear />
          </IconButton>
        </Box>
      )}

      <CookiePreferencesModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={preferences}
        onSave={savePreferences}
      />
    </>
  );
};
